import {
	AfterViewInit,
	Component,
	Inject,
	Optional,
	ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogClose,
	MatDialogActions,
} from '@angular/material/dialog';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { NgIf, NgFor, CurrencyPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatOption } from '@angular/material/select';
interface Payments {
	Amortization_Interest_Monthly: number | null;
	LPP_Monthly: number | null;
	Payment_Amount_Insurance_Monthly: number | null;
	Amortization_Interest_Biweekly: number | null;
	LPP_Biweekly: number | null;
	Payment_Amount_Insurance_Biweekly: number | null;
	Amortization_Interest_Weekly: number | null;
	LPP_Weekly: number | null;
	Payment_Amount_Insurance_Weekly: number | null;
	Amortization_Interest_SemiMonthly: number | null;
	LPP_SemiMonthly: number | null;
	Payment_Amount_Insurance_SemiMonthly: number | null;
}

@Component({
	selector: 'app-partial-waiveoff-modal',
	templateUrl: './partial-waiveoff-modal.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [
		`
			.header {
				display: flex;
				justify-content: space-between;
				width: 100%;
				background-color: #f7f7f7;
			}
			span {
				padding-left: 20px;
				margin-top: 0.75em;
				font-weight: bold;
			}
			.partialDisplay {
				display: flex;
				flex-direction: column;
				padding: 5px 20px;
			}
			table {
				width: 100%;
				border-collapse: collapse;
			}
			tr,
			td,
			th {
				border: 1px solid #999;
			}
			th,
			td:first-child {
				padding-left: 10px;
			}
			td:not(:first-child) {
				text-align: right;
				padding-right: 10px;
			}
		`,
	],
	imports: [
		CdkDrag,
		CdkDragHandle,
		MatIconButton,
		MatDialogClose,
		MatIcon,
		MatFormField,
		MatLabel,
		ReactiveFormsModule,
		MatInput,
		MatButton,
		NgIf,
		MatProgressSpinner,
		NgFor,
		MatSelect,
		MatOption,
		MatDialogActions,
		CurrencyPipe,
	],
})
export class PartialWaiveoffModalComponent implements AfterViewInit {
	constructor(
		private apiService: LegacyApiService,
		public dialogRef: MatDialogRef<PartialWaiveoffModalComponent>,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any
	) {}
	totalBalance: number = 0;
	totalLegalFee: number = 0;
	originalLoanAmount: number = 0;
	newLoanAmount: number = 0;
	partialAppliedFee: number = 0;
	partialRate = new FormControl<number | null>(null, Validators.required);
	fileType = new FormControl('New', Validators.required);
	partialReason = new FormControl('', Validators.required);
	tableHeaders: string[] = ['Frequency', 'P+I', 'LPP', 'Total'];
	previewData?: any;
	fileTypes: string[] = ['New', 'Refinance'];
	verifiedResult: any;
	isLoading: boolean = false;
	ngAfterViewInit(): void {
		console.log(this.data);
		this.totalLegalFee = parseFloat(
			this.data?.loanDebts
				.map((b) => b.Legal_Fee)
				?.reduce((x, y) => x + y, 0)
				.toFixed(2)
		);
		this.totalBalance = parseFloat(
			this.data?.loanDebts
				.map((b) => b.dsLoanAmount)
				?.reduce((x, y) => x + y, 0)
				.toFixed(2)
		);
		this.originalLoanAmount = this.data?.loanInput?.Loan_Amount;
	}

	previewPartial(): void {
		this.partialAppliedFee = parseFloat(
			(this.totalLegalFee * (1 - this.partialRate.value / 100)).toFixed(2)
		);
		this.newLoanAmount = parseFloat(
			(this.totalBalance + this.partialAppliedFee).toFixed(2)
		);
		this.data.loanInput.Loan_Amount = this.newLoanAmount;
		this.isLoading = true;
		if (this.data?.loanInput?.Loan_Type === "'Click and Loan") {
			this.apiService
				.calculate_am_click_loan(this.data.loanInput)
				.subscribe((res) => {
					this.previewData = res;
					this.isLoading = false;
				});
		} else {
			this.apiService.calculate_am(this.data.loanInput).subscribe((res) => {
				this.previewData = res;
				this.isLoading = false;
			});
		}
	}

	preventDecimal(event: KeyboardEvent): void {
		if (event.key === '.' || event.key === ',') {
			event.preventDefault();
		}
	}

	async requestPartialWaiveOff(waiveOffMode: string) {
		this.verifiedResult = await this.verifyWaiveOff(
			this.data?.appInfo.applicationId,
			this.data?.appInfo?.agentId
		);
		const exceptionCounts = this.verifiedResult?.exceptionCount;
		if (this.verifiedResult?.activeCount > 0) {
			this.confirmAction(
				parseInt(exceptionCounts?.partial) + 1,
				`Partial exception Count: ${
					parseInt(exceptionCounts?.partial) + 1
				} \nThis file has ${
					this.verifiedResult?.activeCount
				} active request. \nDo you still want to request ${waiveOffMode} legal fee waive-off?`
			);
		} else {
			this.confirmAction(
				parseInt(exceptionCounts?.partial) + 1,
				`Partial exception Count: ${
					parseInt(exceptionCounts?.partial) + 1
				} \nDo you want to request ${waiveOffMode} legal fee waive-off?`
			);
		}
	}

	verifyWaiveOff(applicationId: string, employeeID: number): Promise<any> {
		return new Promise((resolve, reject) => {
			this.apiService
				.verifyWaiveOff(applicationId, employeeID)
				.subscribe((res: any) => {
					resolve(res);
				});
		});
	}

	confirmAction(exceptionCounts: number, confirmMessage: any) {
		if (confirm(confirmMessage) == true) {
			const requestedData = {
				fileType: this.fileType.value,
				requestedAmount: this.newLoanAmount,
				requestedReason: this.partialReason.value,
				waive_off_mode: 'Partial',
				originalLoanAmount: this.originalLoanAmount,
				waiveOffRate: this.partialRate.value,
			};
			this.data.appInfo.exceptionCount = exceptionCounts;
			const data = Object.assign({}, this.data?.appInfo, requestedData);
			this.apiService.updateSahaException(true, data).subscribe((res: any) => {
				if (res?.message == 'Success') {
					this.dialogRef.close(true);
				} else {
					console.log(res, 'Error submitting partial waive-off request.');
				}
			});
		} else {
			this.dialogRef.close(false);
			console.log('Cancelled');
		}
	}

	closeModal() {
		this.dialogRef.close(false);
	}
}
